// Single entry point for every call to the local print server (port 9632).
// Inside the Electron app the request is relayed through the main process
// (Node http → 127.0.0.1:9632), so Chromium's renderer network policy
// (Private Network Access, CORS, proxy) can never block it. In a plain browser
// it falls back to fetch. Errors mimic axios' shape (`code`, `response.data`)
// so existing call sites keep working unchanged.

const LOCAL_PRINT_SERVER = 'http://localhost:9632';

export const isPrintServerBridged = () => typeof window !== 'undefined' && !!window.electronAPI?.printServerRequest;

function networkError(message, code, extra = {}) {
  const err = new Error(message);
  err.code = 'ERR_NETWORK';
  err.cause = code;
  Object.assign(err, extra);
  return err;
}

function httpError(status, data) {
  const err = new Error(data?.error || `HTTP ${status}`);
  err.status = status;
  err.response = { status, data };
  return err;
}

// V6.54 — store logo on receipts. HardwareSettings / POSScreen call syncReceiptLogoCache(hw) so
// every print job that carries `storeSettings` gets `receipt_logo_base64` attached when enabled.
// V10.24 — receipt margins removed: 80mm output uses the Windows printer-driver defaults only.
const LOGO_FLAG_KEY = 'print_logo_on_receipt';
const LOGO_B64_KEY = 'receipt_logo_b64';

export async function syncReceiptLogoCache(hw) {
  localStorage.removeItem('receipt_margins');
  const on = !!hw?.print_logo_on_receipt;
  localStorage.setItem(LOGO_FLAG_KEY, on ? '1' : '0');
  if (!on) { localStorage.removeItem(LOGO_B64_KEY); return; }
  try {
    const { API } = await import('@/apiConfig');
    const r = await fetch(`${API}/button-images/store-logo/base64`);
    const j = await r.json();
    if (j?.image_base64) localStorage.setItem(LOGO_B64_KEY, j.image_base64);
    else localStorage.removeItem(LOGO_B64_KEY);
  } catch { /* keep whatever is cached */ }
}

function attachReceiptLogo(data) {
  if (!data || typeof data !== 'object' || !('storeSettings' in data)) return data;
  if (localStorage.getItem(LOGO_FLAG_KEY) !== '1') return data;
  const b64 = localStorage.getItem(LOGO_B64_KEY);
  if (!b64) return data;
  return { ...data, storeSettings: { ...(data.storeSettings || {}), receipt_logo_base64: b64 } };
}

export async function printServerRequest(method, path, data, { timeoutMs = 10000 } = {}) {
  data = attachReceiptLogo(data);
  if (isPrintServerBridged()) {
    const res = await window.electronAPI.printServerRequest({ method, path, data, timeoutMs });
    if (!res.ok && res.status === 0) {
      const detail = res.code === 'ETIMEDOUT'
        ? 'timed out'
        : res.code === 'ECONNREFUSED'
          ? `connection refused (print server process ${res.serverProcessAlive ? 'is running but not listening' : 'is not running'})`
          : `${res.code || 'error'}: ${res.error || ''}`;
      throw networkError(`Print server unreachable — ${detail}`, res.code, { serverProcessAlive: res.serverProcessAlive });
    }
    if (!res.ok) throw httpError(res.status, res.data);
    return { status: res.status, data: res.data };
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${LOCAL_PRINT_SERVER}${path}`, {
      method: method.toUpperCase(),
      headers: data !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: data !== undefined ? JSON.stringify(data) : undefined,
      signal: controller.signal,
    });
    const body = await response.json().catch(() => null);
    if (!response.ok) throw httpError(response.status, body);
    return { status: response.status, data: body };
  } catch (error) {
    if (error.response) throw error;
    if (error.name === 'AbortError') throw networkError('Print server unreachable — timed out', 'ETIMEDOUT');
    throw networkError('Print server unreachable — Network Error', 'ERR_NETWORK');
  } finally {
    clearTimeout(timeoutId);
  }
}

export const isPrintServerNetworkError = (error) => error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error');
